/**
 * Ventutto strict, vendor-aware storefront search for Empire 12.
 */
(function () {
  'use strict';

  var config = window.VentuttoSearchConfig || {};
  var vendors = Array.isArray(config.vendors) ? config.vendors : [];
  var predictiveFields = 'title,vendor,product_type,variants.title,variants.sku,tag';

  function words(value) {
    return String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[’']/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(/\s+/)
      .filter(function (word) { return word && word !== 'and'; });
  }

  function distance(a, b) {
    var previous = Array.from({ length: b.length + 1 }, function (_, i) { return i; });

    for (var i = 1; i <= a.length; i += 1) {
      var current = [i];
      for (var j = 1; j <= b.length; j += 1) {
        current[j] = Math.min(
          current[j - 1] + 1,
          previous[j] + 1,
          previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
        );
      }
      previous = current;
    }

    return previous[b.length];
  }

  function closeWord(queryWord, vendorWord) {
    if (queryWord === vendorWord) return true;
    if (queryWord.endsWith('s') && queryWord.slice(0, -1) === vendorWord) return true;
    return queryWord.length >= 5 && vendorWord.length >= 5 && distance(queryWord, vendorWord) <= 1;
  }

  function uniqueVendorNames(names) {
    var seen = {};
    return names.filter(function (name) {
      var key = name.toLowerCase();
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    });
  }

  function vendorWordMatches(queryWord, vendorWord, allowPrefix) {
    return closeWord(queryWord, vendorWord) ||
      (allowPrefix && queryWord.length >= 2 && vendorWord.indexOf(queryWord) === 0);
  }

  function resolveVendors(query) {
    var queryWords = words(query);
    if (!queryWords.length) return { names: [], consumed: 0 };
    var matches = vendors.map(function (vendor) {
      var vendorWords = words(vendor);
      if (!vendorWords.length) return null;
      var count = Math.min(vendorWords.length, queryWords.length);
      var consumed = 0;
      for (var index = 0; index < count; index += 1) {
        if (!vendorWordMatches(queryWords[index], vendorWords[index], index === count - 1)) break;
        consumed += 1;
      }
      if (!consumed) {
        if (queryWords[0].length >= 3 && vendorWords[0].indexOf(queryWords[0]) === 0) consumed = 1;
        else return null;
      }
      if (consumed === 1 && queryWords[0].length < 3 && queryWords[0] !== vendorWords[0]) return null;
      return { name: vendor, consumed: consumed };
    }).filter(Boolean);
    if (!matches.length) return { names: [], consumed: 0 };
    var longest = Math.max.apply(null, matches.map(function (match) { return match.consumed; }));
    return {
      names: uniqueVendorNames(matches.filter(function (match) {
        return match.consumed === longest;
      }).map(function (match) { return match.name; })),
      consumed: longest
    };
  }

  function remainingTerms(query, count) {
    var pattern = /[\p{L}\p{N}]+(?:[’'][\p{L}\p{N}]+)*/gu;
    var match;
    var seen = 0;
    while ((match = pattern.exec(query))) {
      if (!words(match[0]).length) continue;
      seen += words(match[0]).length;
      if (seen >= count) return query.slice(pattern.lastIndex).trim();
    }
    return '';
  }

  function searchTokens(query) {
    return (String(query || '').match(/"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\S+/g) || [])
      .filter(function (token) { return /[\p{L}\p{N}]/u.test(token); })
      .map(function (token) {
        var quoted = token.length > 1 && ((token[0] === '"' && token[token.length - 1] === '"') ||
          (token[0] === "'" && token[token.length - 1] === "'"));
        return { value: quoted ? token.slice(1, -1) : token, quoted: quoted };
      });
  }

  function visibleTerms(query) {
    return String(query || '')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function strictTerms(query) {
    var tokens = visibleTerms(query)
      .match(/"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\S+/g) || [];

    tokens = tokens.filter(function (token) { return /[A-Za-z0-9]/.test(token); });
    return tokens.length > 1 ? tokens.join(' AND ') : (tokens[0] || '');
  }

  function escapeValue(value) {
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function titleQuery(searchWords) {
    var families = {
      shoe: ['shoe', 'sneaker', 'heel', 'pump', 'boot', 'sandal', 'flat', 'loafer', 'oxford', 'derby', 'slipper', 'slide'],
      shoes: ['shoe', 'sneaker', 'heel', 'pump', 'boot', 'sandal', 'flat', 'loafer', 'oxford', 'derby', 'slipper', 'slide'],
      heel: ['heel', 'pump'],
      heels: ['heel', 'pump'],
      pump: ['heel', 'pump'],
      pumps: ['heel', 'pump'],
      shirt: ['shirt', 'tee'],
      shirts: ['shirt', 'tee'],
      tee: ['shirt', 'tee'],
      tees: ['shirt', 'tee'],
      eyewear: ['sunglass', 'eyeglass', 'optical'],
      bag: ['bag', 'handbag', 'tote', 'clutch', 'backpack', 'satchel'],
      bags: ['bag', 'handbag', 'tote', 'clutch', 'backpack', 'satchel'],
      tie: ['tie', 'necktie'],
      ties: ['tie', 'necktie']
    };

    return searchWords.map(function (token) {
      var word = token.value;
      var normalized = words(word)[0] || '';
      // Scarf has an irregular plural; preserve strict title matching for both.
      if (!token.quoted && (normalized === 'scarf' || normalized === 'scarves') && words(word).length === 1) {
        return '(title:"scarf" OR title:"scarves")';
      }
      var family = !token.quoted && words(word).length === 1 ? families[normalized] : null;
      if (family) {
        return '(' + family.map(function (term) {
          return 'title:' + term + '*';
        }).join(' OR ') + ')';
      }
      return '(' + ['title', 'variants.title', 'variants.sku'].map(function (field) {
        return field + ':"' + escapeValue(word) + '"';
      }).join(' OR ') + ')';
    }).join(' AND ');
  }

  function buildQuery(query) {
    var naturalQuery = visibleTerms(query);
    if (!naturalQuery) return '';
    if (/(?:^|[\s(])(?:title|vendor|body|tag|product_type|variants\.(?:title|sku|barcode)):/i.test(naturalQuery)) return query;
    var tokens = searchTokens(naturalQuery);
    if (tokens.some(function (token) { return token.quoted; })) return titleQuery(tokens);
    var matched = resolveVendors(naturalQuery);
    if (!matched.names.length) return titleQuery(searchTokens(naturalQuery));
    var vendorClauses = matched.names.map(function (vendor) {
      return 'vendor:"' + escapeValue(vendor) + '"';
    });
    var vendorQuery = vendorClauses.length === 1 ? vendorClauses[0] : '(' + vendorClauses.join(' OR ') + ')';
    var remaining = searchTokens(remainingTerms(naturalQuery, matched.consumed));
    return remaining.length ? vendorQuery + ' AND ' + titleQuery(remaining) : vendorQuery;
  }

  function isSearchForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    try {
      var action = new URL(form.action, window.location.origin);
      var path = new URL(window.Theme.routes.search_url, window.location.origin).pathname;
      return action.origin === window.location.origin && action.pathname === path;
    } catch (error) {
      return false;
    }
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!isSearchForm(form)) return;
    var input = form.querySelector('input[name="q"]');
    if (input) {
      var original = visibleTerms(input.value);
      var technical = buildQuery(original);
      try {
        sessionStorage.setItem('ventutto_search_display', JSON.stringify({
          original: original,
          technical: technical
        }));
      } catch (error) {
        // Search remains functional when session storage is unavailable.
      }
      var displayInput = form.querySelector('input[name="vtr_query"]');
      if (!displayInput) {
        displayInput = document.createElement('input');
        displayInput.type = 'hidden';
        displayInput.name = 'vtr_query';
        form.appendChild(displayInput);
      }
      displayInput.value = original;
      input.value = technical;
    }
  }, true);

  function matchesSubmittedQuery(submitted, technical) {
    if (submitted === technical) return true;
    var suffix = ' AND ' + technical;
    return /^(?:tag|product_type):/.test(submitted || '') &&
      submitted.slice(-suffix.length) === suffix;
  }

  function restoreDisplayTerms() {
    try {
      var params = new URLSearchParams(window.location.search);
      var submitted = params.get('q');
      var original = params.get('vtr_query');
      var saved = original && matchesSubmittedQuery(submitted, buildQuery(original)) ? { original: original, technical: submitted } : null;
      if (!saved) {
        try { saved = JSON.parse(sessionStorage.getItem('ventutto_search_display')); } catch (error) {}
      }
      if (!saved || !matchesSubmittedQuery(submitted, saved.technical)) return;

      document.querySelectorAll('input[name="q"]').forEach(function (input) {
        input.value = saved.original;
        input.setAttribute('value', saved.original);
      });
      document.title = document.title.replace(/(found for\s+)[\s\S]*(\s[–-]\s[^–]+$)/, function (_, prefix, suffix) {
        return prefix + '"' + saved.original + '"' + suffix;
      });
    } catch (error) {
      // Customer-facing restoration is optional; never interrupt search.
    }
  }

  // This asset loads at the end of the body, after Empire's search fields are
  // present. Restore immediately to avoid a visible technical-query flash,
  // then run once more at DOM ready as a safe fallback.
  restoreDisplayTerms();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restoreDisplayTerms, { once: true });
  }

  if (typeof window.fetch === 'function') {
    var nativeFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      var requestUrl = typeof input === 'string' ? input : input instanceof URL ? input.href : input && input.url;
      if (!requestUrl) return nativeFetch(input, init);

      try {
        var url = new URL(requestUrl, window.location.origin);
        var path = new URL(window.Theme.routes.predictive_search_url, window.location.origin).pathname;
        if (url.origin === window.location.origin && (url.pathname === path || url.pathname === path + ".json")) {
          url.searchParams.set('q', buildQuery(url.searchParams.get('q')));
          url.searchParams.set('resources[type]', 'product');
          url.searchParams.set('resources[options][fields]', predictiveFields);
          input = typeof input === 'string' || input instanceof URL ? url.toString() : new Request(url.toString(), input);
        }
      } catch (error) {
        // Preserve Empire's original request if it cannot be decorated safely.
      }

      return nativeFetch(input, init);
    };
  }
}());
